/**
 * Landing-page preference (client side).
 *
 * The authoritative list lives on the backend (`backend/utils/landingPages.js`)
 * and is served by `GET /api/auth/landing-pages`, which Settings uses to build
 * its options. This module holds the id → path mapping the login redirect needs
 * synchronously, before any request could return.
 *
 * Keep the ids in step with the backend. An id that isn't here resolves to the
 * dashboard rather than navigating somewhere broken.
 */

const LANDING_PATHS = Object.freeze({
  dashboard: '/',
  story: '/story',
  holdings: '/holdings/stocks',
  analytics: '/analytics/performance',
  insights: '/insights',
});

const DEFAULT_LANDING_PAGE = 'dashboard';

/** Key written by the previous, device-local "skip the story" toggle. */
const LEGACY_SKIP_STORY_KEY = 'portfolio-tracker:skip-story';

/**
 * Resolve a landing-page id to the route to open after login.
 *
 * @param {string|undefined|null} id
 * @returns {string}
 */
export function landingPathFor(id) {
  return LANDING_PATHS[id] || LANDING_PATHS[DEFAULT_LANDING_PAGE];
}

/**
 * Where this user should land, given the profile returned by login or `/me`.
 *
 * @param {Object|null|undefined} user
 * @returns {string}
 */
export function landingPathForUser(user) {
  return landingPathFor(user?.preferences?.landingPage);
}

/**
 * Drop the superseded localStorage flag.
 *
 * The preference now lives on the profile, so leaving the old key behind would
 * be a second source of truth that silently does nothing. Called once on app
 * start; safe to call repeatedly, and safe where localStorage throws (Safari
 * private mode).
 */
export function clearLegacyStoryPreference() {
  try {
    window.localStorage.removeItem(LEGACY_SKIP_STORY_KEY);
  } catch {
    // Nothing to clean up on a device that cannot read localStorage.
  }
}

export { LANDING_PATHS, DEFAULT_LANDING_PAGE, LEGACY_SKIP_STORY_KEY };
